import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchChallenge, login } from '../utils/api';
import { useI18n } from '../contexts/I18nContext';
import { sha256Hex } from '../utils/sha256';
import './Login.css';

// SHA-256 hex of a string. Prefers crypto.subtle, which is only available in
// secure contexts (HTTPS or localhost); over plain HTTP on a real hostname it
// is undefined, so fall back to a dependency-free JS implementation.
const toHex = buf => Array.from(new Uint8Array(buf))
  .map(b => b.toString(16).padStart(2, '0')).join('');
const hashHex = (window.crypto && window.crypto.subtle)
  ? async (bytes) => toHex(await window.crypto.subtle.digest('SHA-256', bytes))
  : async (bytes) => sha256Hex(bytes);

// Async PoW using SHA-256 — yields to event loop every 50 hashes
async function computePoW(challenge, difficulty, onProgress, cancelRef) {
  const prefix = '0'.repeat(difficulty);
  const enc = new TextEncoder();

  let nonce = 0;
  const BATCH = 50;

  while (true) {
    if (cancelRef.current) return null;

    for (let i = 0; i < BATCH; i++) {
      if (cancelRef.current) return null;
      const hex = await hashHex(enc.encode(challenge + nonce));
      if (hex.startsWith(prefix)) return { nonce, hash: hex };
      nonce++;
    }

    onProgress(nonce);
    // Yield to the browser event loop
    await new Promise(r => setTimeout(r, 0));
  }
}

export default function Login({ onLogin }) {
  const { t } = useI18n();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [honeypot, setHoneypot] = useState('');   // must stay empty
  const [challenge, setChallenge] = useState(null);
  const [powResult, setPowResult] = useState(null);
  const [powProgress, setPowProgress] = useState(0);
  const [powDone, setPowDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const cancelRef = useRef(false);
  const startedAt = useRef(Date.now());

  const startPoW = useCallback(async (ch) => {
    cancelRef.current = false;
    setPowDone(false);
    setPowResult(null);
    setPowProgress(0);
    startedAt.current = Date.now();

    const result = await computePoW(ch.challenge, ch.difficulty, setPowProgress, cancelRef);
    if (result) {
      setPowResult(result);
      setPowDone(true);
    }
  }, []);

  useEffect(() => {
    fetchChallenge()
      .then(ch => { setChallenge(ch); startPoW(ch); })
      .catch(() => setError(t('login.connect_error')));
    return () => { cancelRef.current = true; };
  }, [startPoW, t]);

  const refreshChallenge = useCallback(async () => {
    cancelRef.current = true;
    setPowDone(false);
    setPowResult(null);
    try {
      const ch = await fetchChallenge();
      setChallenge(ch);
      await startPoW(ch);
    } catch {
      setError(t('login.challenge_error'));
    }
  }, [startPoW, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (honeypot) return;
    if (!powDone || !challenge || !powResult) return;
    if (!username.trim() || !password) return;

    setLoading(true);
    setError('');
    try {
      const data = await login(
        username.trim(), password,
        challenge.challenge, powResult.nonce, powResult.hash
      );
      onLogin(data);
    } catch (err) {
      setError(err.message);
      await refreshChallenge();
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = powDone && !loading && username.trim() && password;

  // PoW progress bar — expected average 2^(difficulty*4) iterations
  const expectedIter = Math.pow(2, challenge ? challenge.difficulty * 4 : 12);
  const progressPct = powDone ? 100 : Math.min(95, (powProgress / expectedIter) * 100);

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <div className="login-brand-icon">⬡</div>
          <div>
            <div className="login-brand-name">AnnotateX</div>
            <div className="login-brand-sub">{t('login.brand_sub')}</div>
          </div>
        </div>

        <form className="login-form" onSubmit={handleSubmit} autoComplete="off">
          {/* Honeypot — bots fill this; humans never see it */}
          <input
            className="login-honeypot"
            type="text"
            name="website"
            value={honeypot}
            onChange={e => setHoneypot(e.target.value)}
            tabIndex={-1}
            aria-hidden="true"
            autoComplete="off"
          />

          <div className="login-field">
            <label>{t('login.username')}</label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="admin"
              autoFocus
              autoComplete="username"
            />
          </div>

          <div className="login-field">
            <label>{t('login.password')}</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>

          <div className="login-pow">
            <div className="pow-bar-wrap">
              <div
                className={`pow-bar-fill ${powDone ? 'done' : 'active'}`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="pow-label">
              {powDone
                ? <span className="pow-ok">{t('login.pow_done')}</span>
                : <span className="pow-wait">{t('login.pow_wait')}<span className="pow-dots" /></span>
              }
            </div>
          </div>

          {error && <div className="login-error">{error}</div>}

          <button type="submit" className="btn btn-primary login-btn" disabled={!canSubmit}>
            {loading ? t('login.loading') : t('login.submit')}
          </button>
        </form>

        <div className="login-hint">
          Proteção por Prova de Trabalho (PoW) + honeypot — sem dependências externas
        </div>
      </div>
    </div>
  );
}
